import React from "react";
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Link,
  Paper,
  Grid,
  Icon,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Gavel,
  AccountCircle,
  Apps,
  Payment,
  Copyright,
  Support,
  CloudDone,
  Block,
  Balance,
  LocationOn,
} from "@mui/icons-material";

export const Terms: React.FC = () => {
  const termsContent = [
    {
      title: "Acceptance of Terms",
      icon: <Gavel />,
      content:
        "By creating an account or using VenFlows, you agree to abide by these terms and all applicable laws. If you do not agree, please do not use our services.",
    },
    {
      title: "Your Account",
      icon: <AccountCircle />,
      sections: [
        {
          content:
            "• You are responsible for all activities under your account.",
        },
        {
          content: "• You must keep your credentials secure.",
        },
        {
          content:
            "• You agree not to misuse or attempt unauthorized access to the platform.",
        },
      ],
    },
    {
      title: "Use of Services",
      icon: <Apps />,
      content:
        "VenFlows is licensed, not sold. You may use it only as permitted:",
      sections: [
        {
          content: "• For business purposes.",
        },
        {
          content: "• As per the subscribed plan/features.",
        },
        {
          content: "• In compliance with local and international laws.",
        },
      ],
      note: "We reserve the right to suspend accounts that abuse the system or break terms.",
    },
    {
      title: "Subscriptions & Payments",
      icon: <Payment />,
      sections: [
        {
          content: "• Some features require a paid subscription.",
        },
        {
          content: "• Payments are processed securely.",
        },
        {
          content:
            "• Subscription terms (monthly, annually, etc.) will be defined during sign-up.",
        },
        {
          content:
            "• Failure to pay on time may result in limited or suspended access.",
        },
      ],
    },
    {
      title: "Intellectual Property",
      icon: <Copyright />,
      content:
        "All intellectual property — including code, design, branding, and features — belongs to Venustusy IT Solutions. You are not permitted to reverse engineer, copy, or redistribute the platform.",
    },
    {
      title: "Data Ownership",
      icon: <CloudDone />,
      sections: [
        {
          content:
            "• All business data (e.g., transactions, inventory) entered by you remains yours.",
        },
        {
          content:
            "• We only use it to operate the service and improve performance.",
        },
      ],
    },
    {
      title: "Support",
      icon: <Support />,
      sections: [
        {
          content: "We offer support through:",
        },
        {
          content: "• Email: support@venustusy.com",
        },
        {
          content: "• Response time: Typically within 24 hours (weekdays).",
        },
      ],
    },
    {
      title: "Service Availability",
      icon: <CloudDone />,
      content:
        "We strive to keep VenFlows available 24/7. However, we may perform maintenance or updates which could cause brief downtime. Emergency incidents will be communicated via email or status page.",
    },
    {
      title: "Termination",
      icon: <Block />,
      content:
        "You may cancel your subscription anytime. We reserve the right to suspend or terminate access for misuse, illegal activity, or breach of terms.",
    },
    {
      title: "Limitation of Liability",
      icon: <Balance />,
      content:
        "To the maximum extent permitted by law, Venustusy IT Solutions will not be liable for indirect, incidental, or consequential damages related to your use of VenFlows.",
    },
    {
      title: "Governing Law",
      icon: <Gavel />,
      content:
        "These terms are governed by the laws of the Kingdom of Bahrain. Any legal matters will be handled in Bahrain's courts.",
    },
  ];

  return (
    <Box sx={{ pb: 8 }}>
      <Box
        sx={{
          background: "linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)",
          borderBottom: 1,
          borderColor: "divider",
          py: { xs: 8, md: 12 },
          mb: 6,
        }}
      >
        <Container maxWidth="lg">
          <Breadcrumbs sx={{ mb: 4 }}>
            <Link component={RouterLink} to="/" color="inherit">
              Home
            </Link>
            <Typography color="text.primary">Terms & Conditions</Typography>
          </Breadcrumbs>

          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "2rem", md: "2.5rem" },
              fontWeight: 800,
              mb: 2,
              background: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Terms & Conditions
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ maxWidth: 800, fontSize: { xs: "1rem", md: "1.25rem" } }}
          >
            Welcome to VenFlows, an ERP platform developed by Venustusy IT
            Solutions, based in Bahrain. By using our platform, you agree to
            these terms. Please read them carefully.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {termsContent.map((section, index) => (
            <Grid item xs={12} key={section.title}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "16px",
                    "&:hover": {
                      borderColor: "primary.main",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "12px",
                        background:
                          "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                      }}
                    >
                      {section.icon}
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: "1.5rem", md: "1.75rem" },
                      }}
                    >
                      {section.title}
                    </Typography>
                  </Box>

                  {section.content && (
                    <Typography
                      color="text.secondary"
                      sx={{ lineHeight: 1.7, fontSize: "1rem" }}
                    >
                      {section.content}
                    </Typography>
                  )}

                  {section.sections && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        mt: section.content ? 2 : 0,
                      }}
                    >
                      {section.sections.map((subsection, idx) => (
                        <Box key={idx}>
                          <Typography
                            color="text.secondary"
                            sx={{ lineHeight: 1.7, fontSize: "1rem" }}
                          >
                            {subsection.content}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {section.note && (
                    <Typography
                      sx={{
                        mt: 2,
                        fontSize: "0.9rem",
                        color: "text.secondary",
                        fontStyle: "italic",
                      }}
                    >
                      {section.note}
                    </Typography>
                  )}
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 6 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Last updated: {new Date().toLocaleDateString()}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              For questions or legal concerns, contact us at{" "}
              <Link href="mailto:support@venustusy.com" color="primary">
                support@venustusy.com
              </Link>
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <LocationOn sx={{ fontSize: 16 }} />
              Bahrain, Manama – Exhibitions Avenue
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};
